import '../../themes/confirm_hyg.scss';
import '../../themes/global.scss';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router';
import { clearSuccess } from '~/actions/success';
import { BackButton, Box, Column, Icon } from '~/commonComponents';
import { BestInDentalProfessionalPopup } from '~/commonComponents/popups/BestInDentalProfessionalPopup';
import { PreApprovedChargeTimelineBanner } from '~/components/ConfirmCandidate/components/PreApprovedChargeTimelineBanner';
import browserHistory from '~/browserHistory';
import { useIsError, useIsSuccess, useNavigationDetector } from '~/hooks';
import ProfessionalBestInDentalWinnerRibbon from '~/images/best-in-dental-professional-ribbon.png';
import {
  CONFIRM_JOB_ACTION,
  DECLINE_CANDIDATE,
  FETCH_JOB_ACTION,
  FETCH_JOB_CANDIDATES_ACTION,
  fetchCandidateJobOvertime,
  fetchJob,
  GET_ALL_PAYMENTS_ACTION,
  getAllPaymnets,
  hidePaymentMissingPopup,
  selectOffice,
} from '../../actions';
import { createSuccessSelector } from '../../api/selectors';
import { CardSpinner } from '../../commonComponents';
import { CandidateSortAnnouncementPopup } from '../../commonComponents/popups/CandidateSortAnnouncementPopup/CandidateSortAnnouncementPopup';
import { BestInDentalStatus } from '../../enums/BestInDental';
import {
  getShouldEnableCandidatesListSort,
  getShouldShowBackgroundCheckBadge,
} from '../../growthbook';
import useLoading from '../../hooks/useLoading';
import WarningIcon from '../../images/icons/Warning';
import { BackgroundCheckStatusEnum } from '../../models';
import { Colors } from '../../themes/colors';
import { FilterStatusEnum } from '../../utils/Enum';
import { ProfessionalBestInDentalWinnerTooltip } from '../BestInDental';
import { PaymentWarningBanner } from '../PaymentWarningBanner';
import { RejectCandidateSuccessPopup } from '../PermanentJobsV2/JobOverview';
import { CandidateBadges } from '../candidates/CandidateBadges';
import ConfirmPopup from '../shared/ConfirmPopup';
import { CandidateHeader } from './components/CandidateHeader';
import { CandidateSelection } from './components/CandidateSelection/CandidateSelection';
import { CollapsibleCandidateHeader } from './components/CandidateSelection/CollapsibleCandidateHeader';
import { CollapsibleCandidateSelection } from './components/CandidateSelection/CollapsibleCandidateSelection';
import { Filters } from './components/CandidateSelection/Filters';
import { ChargeTimelineCollapsibleBanner } from './components/ChargeTimelineCollapsibleBanner';
import { ConfirmCandidateAnotherJobPopup } from './components/ConfirmCandidateAnotherJobPopup';
import { ConfirmCandidateEligibleJobsPopup } from './components/ConfirmCandidateEligibleJobsPopup';
import { ShiftDetails } from './components/ShiftDetails';
import { WarningBanner } from './components/WarningBanner';
import { fireConfirmCandidateCandidateViewed } from '~/posthogs/events';
import { useJobCandidates } from './hooks/useJobCandidates';
import { DeclineCandidateSuccessPopup } from '../PermanentJobsV2/JobOverview/components';
import { useFeatureFlag } from '~/hooks/useFeatureFlag';
import { EnablePermanentJobM2M } from '~/constants/ff.posthog';
import { ExperienceOverview } from '../CandidateOverview/containers';
import { useGetAuthorizationProfessional } from '~/domain/Credentials/usecases/useGetAuthorizationProfessional';
import { VerifiedCredentials } from '~/components/ProfessionalProfile/components/ProfileCard/components/VerifiedCredentials';

const actions = [CONFIRM_JOB_ACTION, FETCH_JOB_ACTION, DECLINE_CANDIDATE, GET_ALL_PAYMENTS_ACTION];
const successSelector = createSuccessSelector([FETCH_JOB_ACTION]);

export const ConfirmCandidate = ({ isSingleView = false }) => {
  const navigate = useNavigate();
  const params = useParams();
  const location = useLocation();
  const dispatch = useDispatch();
  const jobId = location.state?.jobId || params.jobId;
  const user = useSelector((state) => state.user.selectedChildOffice ?? state.user.user);
  const [notesForTemp, setNotesForTemp] = useState(user.notes_for_temp || '');
  const { job, allCandidates, visibleCandidates } = useSelector((state) => state.job);
  const {
    fetchCandidates,
    reloadCandidates,
    isLoading: loadingCandidates,
    isSuccessFetchJobCandidates,
  } = useJobCandidates();

  const isOnsiteInterview = job?.isOnsiteInterview;

  const isEnablePermanentJobM2M = useFeatureFlag(EnablePermanentJobM2M);

  const { isLoading } = useLoading(actions);
  const { isSuccess: isDeclineCandidateSuccess, clearSuccess: clearDeclineCandidateSuccess } =
    useIsSuccess([DECLINE_CANDIDATE]);

  const isShowPaymentMissingPopup = useSelector(
    (state) => state.payments.isShowPaymentMissingPopup,
  );
  const candidateId = params?.candidateId;
  const currentCounter = allCandidates?.find((counter) => counter?.candidate?.id === candidateId);
  const waitSuccessFetchJobRequest = useSelector((state) => successSelector(state));
  const isFirstRenderRef = useRef(true);
  const { show } = useSelector((state) => state.job.confirmCandidateAnotherJobPopup);
  const { show: showConfirmCandidateEligibleJobsPopup } = useSelector(
    (state) => state.job.confirmCandidateEligibleJobsPopup,
  );
  const candidate = currentCounter?.candidate;

  const { displayProfession, authorizations, isCredentialV2 } = useGetAuthorizationProfessional({
    professionalId: candidateId ?? '',
    candidateLicense: candidate?.license || candidate?.licenses?.[0],
    jobProfessionName: job?.professionName,
    jobSubProfession: job?.subProfession,
    offerOwnerId: job?.offer_owner?.id,
    jobProfession: job?.profession,
    jobState: job?.state,
  });

  const isCandidateListSortEnabled = getShouldEnableCandidatesListSort();

  const candidateRef = useRef(candidate);

  const [showCollapsibleSelection, setShowCollapsibleSelection] = useState(false);
  const [showCollapsibleHeader, setShowCollapsibleHeader] = useState(false);
  const hasMultipleCounters = allCandidates?.length > 1;
  const isFilterEnabled = isCandidateListSortEnabled && allCandidates?.length > 1;

  const candidateFetchRef = useRef(false);
  const candidateSelectionRef = useRef(null);
  const candidateHeaderRef = useRef(null);
  const hasMultipleCountersRef = useRef(false);

  const handleDeclineConfirmed = useCallback((hasMultiple) => {
    hasMultipleCountersRef.current = hasMultiple;
  }, []);

  const [confirmationExitModalOpen, setConfirmationExitModalOpen] = useState(false);
  const [shouldBlockNavigation, setShouldBlockNavigation] = useState(true);
  const hasCandidatesAvailable = allCandidates?.length > 0;
  const { isError } = useIsError(actions);

  const isBestInDentalWinner = useMemo(() => {
    return candidate?.professional_info?.bestInDental?.status === BestInDentalStatus.Winner;
  }, [candidate]);

  useEffect(() => {
    if (candidate) {
      candidateRef.current = candidate;
    }
  }, [candidate]);

  useEffect(() => {
    if (candidate && allCandidates?.length && jobId) {
      const candidateIndex = allCandidates.findIndex(
        (counter) => counter?.candidate?.id === candidate.id,
      );
      fireConfirmCandidateCandidateViewed({
        shiftId: jobId,
        candidateNumber: candidateIndex + 1,
        candidateCount: allCandidates.length,
      });
    }
  }, [candidate?.id, jobId]);

  const { allowNavigation, blockNavigation, clearPendingNavigation, navigateToPending } =
    useNavigationDetector({
      resetKey: isError,
      when: hasCandidatesAvailable && shouldBlockNavigation,
      onNavigationAttempt: (targetLocation) => {
        // Check if navigation is to another candidate on the same job or to the candidate profile screen
        const candidateScreen = `/dashboard/candidate/${jobId}`;
        const currentJobPattern = `/dashboard/job/${jobId}`;
        const targetPath = targetLocation.pathname;

        // If navigating to another candidate on the same job or to the candidate profile screen, allow it
        if (targetPath.startsWith(currentJobPattern) || targetPath.startsWith(candidateScreen)) {
          allowNavigation();
          browserHistory.push(targetLocation.pathname, targetLocation.state);
          return;
        }

        // Otherwise, show the confirmation popup
        setConfirmationExitModalOpen(true);
        blockNavigation();
      },
    });

  const handleNavigateWithoutBlocking = useCallback(
    (path) => {
      // Disable blocking temporarily
      setShouldBlockNavigation(false);
      clearPendingNavigation();

      // Navigate after ensuring the blocker is disabled
      setTimeout(() => {
        browserHistory.push(path);
        // Re-enable blocking after navigation (in case user navigates back)
        setTimeout(() => {
          setShouldBlockNavigation(true);
        }, 100);
      }, 0);
    },
    [clearPendingNavigation],
  );

  const handleScroll = useCallback(() => {
    if (isSingleView) return;

    if (candidateSelectionRef.current) {
      const scrollPosition = window.scrollY;

      const candidateSelectionBottom =
        candidateSelectionRef.current.offsetTop + candidateSelectionRef.current.offsetHeight;

      if (scrollPosition >= candidateSelectionBottom + 5) {
        setShowCollapsibleSelection(true);
      } else {
        setShowCollapsibleSelection(false);
      }
    }

    if (candidateHeaderRef.current) {
      const scrollPosition = window.scrollY;
      const candidateHeaderBottom =
        candidateHeaderRef.current.offsetTop + candidateHeaderRef.current.offsetHeight;

      if (scrollPosition >= candidateHeaderBottom + (!hasMultipleCounters ? 100 : 220)) {
        setShowCollapsibleHeader(true);
      } else {
        setShowCollapsibleHeader(false);
      }
    }
  }, [hasMultipleCounters]);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);

    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  useEffect(() => {
    if (jobId !== job?.id || isFirstRenderRef.current) {
      dispatch(
        fetchJob({ jobId, candidateId: params?.candidateId, requestCandidateOvertime: false }),
      );
      isFirstRenderRef.current = false;
    } else if (
      waitSuccessFetchJobRequest &&
      !isFirstRenderRef.current
    ) {
      dispatch(fetchCandidateJobOvertime({ jobId, candidateId: params?.candidateId }));
    }
  }, [dispatch, jobId, params.candidateId, job?.id, waitSuccessFetchJobRequest]);

  useEffect(() => {
    if (job && job.id !== jobId) {
      candidateFetchRef.current = false;
      return;
    }

    if (!waitSuccessFetchJobRequest || jobId !== job?.id) {
      return;
    }
    const backFromCandidateProfile =
      allCandidates?.length && visibleCandidates?.length && browserHistory.action === 'POP';

    if (!jobId || candidateFetchRef.current) {
      return;
    }

    if (backFromCandidateProfile) {
      candidateFetchRef.current = true;
      return;
    }

    const specialty = job?.specialty;
    if (!specialty) {
      return;
    }

    fetchCandidates(jobId, specialty);
    dispatch(getAllPaymnets(job?.offer_owner?.id));

    candidateFetchRef.current = true;
  }, [
    jobId,
    job?.id,
    job?.specialty,
    fetchCandidates,
    reloadCandidates,
    allCandidates,
    visibleCandidates,
    browserHistory,
    location.state,
    params.jobId,
    waitSuccessFetchJobRequest,
  ]);

  const handleBackButton = () => {
    if (location.state?.fromBulkConfirmation) {
      navigate(-1);
    } else {
      navigate('/dashboard/jobs/pending');
    }
  };

  useEffect(() => {
    // Reload candidates list when coming back from DeclineCandidate action
    const { jobId } = params;
    const fromDeclineCandidate = location.state?.fromDeclineCandidate;

    if (jobId && fromDeclineCandidate) {
      reloadCandidates(jobId);
    }
  }, [params, location.state, reloadCandidates]);

  if (show || showConfirmCandidateEligibleJobsPopup) {
    return (
      <>
        <CardSpinner />
        <ConfirmCandidateAnotherJobPopup />
        <ConfirmCandidateEligibleJobsPopup
          navigateWithoutBlocking={handleNavigateWithoutBlocking}
        />
      </>
    );
  }

  if (isLoading || loadingCandidates || !job || !job.best_counter || !candidate) {
    if (jobId === job?.id && job?.status === FilterStatusEnum.SCHEDULED) {
      browserHistory.replace(`/dashboard/job/${job.id}/details`);
      return <></>;
    }

    if (
      job &&
      job.status === FilterStatusEnum.OPEN &&
      isSuccessFetchJobCandidates &&
      !hasCandidatesAvailable &&
      !isOnsiteInterview // skip navigation if onsite interview (regardless of success state)
    ) {
      navigate('/');
      dispatch(clearSuccess([...actions, FETCH_JOB_CANDIDATES_ACTION]));
      return <></>;
    }

    return (
      <>
        <CardSpinner />
        {isDeclineCandidateSuccess && isOnsiteInterview && (
          // placed right here because every time using Monolith Decline Candidate it will force trigger loading whole screen
          <>
            {isEnablePermanentJobM2M ? (
              <DeclineCandidateSuccessPopup
                professionalName={`${candidateRef.current?.first_name} ${candidateRef.current?.last_name}`}
                onClose={() => {
                  allowNavigation();
                  if (!hasMultipleCountersRef.current) {
                    navigate(`/permanent-jobs/job-overview/${job?.postingId}`, { replace: true });
                  } else {
                    navigate(`/dashboard/job/${jobId}`, { replace: true });
                  }
                  clearDeclineCandidateSuccess();
                }}
              />
            ) : (
              <RejectCandidateSuccessPopup
                professionalName={`${candidateRef.current?.first_name} ${candidateRef.current?.last_name}`}
                onClose={() => {
                  allowNavigation();
                  navigate(`/permanent-jobs/job-overview/${job?.postingId}`, { replace: true });
                  clearDeclineCandidateSuccess();
                }}
              />
            )}
          </>
        )}
      </>
    );
  }

  return (
    <div className="component_container global_font">
      <div className="floating_container" style={{ justifyContent: 'center' }}>
        <div
          style={{
            width: '100%',
            maxWidth: 1200,
            alignItems: 'center',
            position: 'relative',
            marginBottom: '40px',
            marginInline: '20px',
          }}
        >
          <PaymentWarningBanner
            officeId={job?.offer_owner?.id}
            containerStyle={{ marginTop: '-10px', marginInline: '-30px' }}
          />
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ flex: 1 }}>
              <BackButton onClick={handleBackButton} />
            </div>

            <h1
              className="bold"
              style={{ flex: 1, textAlign: 'center', color: Colors.neutral_600 }}
            >
              Confirm Candidate
            </h1>

            <div style={{ flex: 1 }}>{isFilterEnabled && !isSingleView && <Filters />}</div>
          </div>

          {!isSingleView && (
            <div ref={candidateSelectionRef} style={{ position: 'relative' }}>
              <div style={{ opacity: showCollapsibleSelection ? 0 : 1 }}>
                <CandidateSelection />
              </div>
              <CollapsibleCandidateSelection show={showCollapsibleSelection} />
            </div>
          )}

          <div
            style={{
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              marginTop: -1,
              backgroundColor: Colors.white,
              boxShadow:
                '0px 3px 10px 1px rgba(0, 0, 0, 0.15), 0px 1px 2px 0px rgba(0, 0, 0, 0.15)',
              position: 'relative',
            }}
          >
            <CollapsibleCandidateHeader
              show={showCollapsibleHeader}
              candidate={candidate}
              notesForTemp={notesForTemp}
              allowNavigation={allowNavigation}
              onDeclineConfirmed={handleDeclineConfirmed}
            />

            <div style={{ padding: 20, position: 'relative' }}>
              <div style={{ opacity: showCollapsibleHeader ? 0 : 1 }}>
                <CandidateHeader
                  ref={candidateHeaderRef}
                  candidate={{
                    ...candidate,
                    hasWorkedWithDSO: currentCounter?.hasWorkedWithDSO,
                    hasWorkedWithOffice: currentCounter?.hasWorkedWithOffice,
                  }}
                  isHasBackgroundCheck={
                    currentCounter?.requestor?.backgroundCheck?.status ===
                      BackgroundCheckStatusEnum.Clear && getShouldShowBackgroundCheckBadge()
                  }
                  displayProfession={displayProfession}
                  notesForTemp={notesForTemp}
                  allowNavigation={allowNavigation}
                  onDeclineConfirmed={handleDeclineConfirmed}
                  job={job}
                />
              </div>

              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1.25fr 1fr',
                  gap: 20,
                  padding: '0 20px',
                }}
              >
                <Box minWidth={0} wordBreak="break-all" gap={20}>
                  <Column>
                    {isBestInDentalWinner && (
                      <ProfessionalBestInDentalWinnerTooltip
                        Anchor={
                          <img
                            alt="Best In Dental Winner Ribbon"
                            src={ProfessionalBestInDentalWinnerRibbon}
                            style={{ width: 184, height: 24 }}
                          />
                        }
                        style={{ container: { height: 24, marginLeft: 'auto' } }}
                      />
                    )}
                    <CandidateBadges candidate={candidate} />
                    <WarningBanner candidate={candidate} job={job} />
                  </Column>
                  {isCredentialV2 && (
                    <VerifiedCredentials authorizations={authorizations} />
                  )}
                  <ExperienceOverview
                    hasWorkedWithDSO={currentCounter?.hasWorkedWithDSO}
                    hasWorkedWithOffice={currentCounter?.hasWorkedWithOffice}
                    job={job}
                    professional={candidate}
                  />
                </Box>
                <div>
                  <ShiftDetails currentCounter={currentCounter} />

                  <ChargeTimelineCollapsibleBanner />

                  <PreApprovedChargeTimelineBanner />

                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      margin: '20px 0px',
                      flex: 1,
                    }}
                  >
                    <h1 className="bold_font" style={{ margin: 10, color: Colors.neutral_600 }}>
                      Notes for Dental Professional:
                    </h1>
                    <textarea
                      className="text_notes"
                      style={{
                        borderWidth: 0,
                        borderRadius: 10,
                        backgroundColor: Colors.neutral_50,
                        boxShadow: '0px 2px 4px 0px rgba(0, 0, 0, 0.25) inset',
                        padding: '12px 20px',
                        width: 'auto',
                        height: 80,
                      }}
                      placeholder="Check in at front desk"
                      value={notesForTemp}
                      onChange={(e) => setNotesForTemp(e.target.value)}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {isShowPaymentMissingPopup ? (
        <ConfirmPopup
          Icon={() => (
            <div style={{ marginBottom: 20 }}>
              <WarningIcon width={64} height={64} color={Colors.warning_500} />
            </div>
          )}
          title="Payment Method Missing"
          description='Please add your preferred payment method to "Confirm" or "Decline" a candidate.'
          descriptionStyle={{ width: '76%', marginBottom: 50 }}
          leftButtonText="Go Back"
          leftButtonAction={() => hidePaymentMissingPopup()}
          rightButtonText="Add Payment"
          rightButtonAction={() => {
            allowNavigation();
            dispatch(hidePaymentMissingPopup());
            dispatch(
              selectOffice({ officeId: job?.offer_owner?.id, redirectUrl: '/account/payment' }),
            );
          }}
        />
      ) : null}

      {isFilterEnabled && <CandidateSortAnnouncementPopup />}

      {confirmationExitModalOpen && (
        <ConfirmPopup
          Icon={() => <Icon name="warning-outline" size={80} />}
          leftButtonAction={() => {
            clearPendingNavigation();
            setConfirmationExitModalOpen(false);
          }}
          leftButtonText="Go Back"
          rightButtonAction={navigateToPending}
          rightButtonText="Leave Anyway"
          title="Candidate Not Confirmed"
          titleStyle={{ marginBottom: 20, marginTop: 20 }}
          descriptionStyle={{ marginBottom: 40 }}
          description="Are you sure you want to leave without confirming someone? Your top candidates are still available but they could get hired by another office at any moment."
        />
      )}

      <BestInDentalProfessionalPopup candidateId={candidateId} />
    </div>
  );
};
